import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  NumberInput,
  SegmentedControl,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronDown,
  IconPointerPlus,
  IconPlus,
  IconRotateClockwise2,
  IconSearch,
  IconSettings,
  IconX,
} from "@tabler/icons-react";
import React from "react";
import { Link } from "react-router-dom";

interface FloorplanActionBarProps {
  selectedFloor: string;
  roomSearchText: string;
  floorplanRotation: number;
  hasSelectedRoom: boolean;
  selectedRoomCount: number;
  showLengthItemsInSelectedRoom: boolean;
  useIfcCoveringCeilingGrids: boolean;
  showDeveloperControls: boolean;
  onRoomSearchTextChange: (value: string) => void;
  onFloorplanRotationChange: (value: number) => void;
  onShowLengthItemsInSelectedRoomChange: (value: boolean) => void;
  onUseIfcCoveringCeilingGridsChange: (value: boolean) => void;
  onCreateIssueForSelectedRooms: () => void;
  onConnectExistingIssueForSelectedRooms: () => void;
  onClearSelectedRooms: () => void;
  manageIfcFilesUrl?: string;
}

const FloorplanActionBar: React.FC<FloorplanActionBarProps> = ({
  selectedFloor,
  roomSearchText,
  floorplanRotation,
  hasSelectedRoom,
  selectedRoomCount,
  showLengthItemsInSelectedRoom,
  useIfcCoveringCeilingGrids,
  showDeveloperControls,
  onRoomSearchTextChange,
  onFloorplanRotationChange,
  onShowLengthItemsInSelectedRoomChange,
  onUseIfcCoveringCeilingGridsChange,
  onCreateIssueForSelectedRooms,
  onConnectExistingIssueForSelectedRooms,
  onClearSelectedRooms,
  manageIfcFilesUrl,
}) => {
  const hasSelectedRooms = selectedRoomCount > 0;

  return (
    <div
      style={{
        width: "100%",
        flexShrink: 0,
        background: "var(--mantine-color-gray-1)",
        borderBottom: "1px solid var(--mantine-color-gray-3)",
        padding: "0.5rem 0.75rem",
      }}
    >
      <Group justify="space-between" align="center" wrap="wrap" gap="xs">
        <Text fw={600}>{selectedFloor || "Ingen etasje valgt"}</Text>

        <Group gap="xs" wrap="nowrap">
          {hasSelectedRoom && !hasSelectedRooms && (
            <Badge
              variant="light"
              color="blue"
              size="lg"
              leftSection={<IconPointerPlus size={14} />}
              styles={{
                root: {
                  background: "var(--mantine-color-blue-0)",
                  color: "var(--mantine-color-blue-8)",
                  border: "1px solid var(--mantine-color-blue-2)",
                },
              }}
            >
              Shift + klikk for flere rom
            </Badge>
          )}

          {hasSelectedRoom && (
            <div
              style={{
                background: "white",
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "6px",
                padding: "0.35rem 0.6rem",
              }}
            >
              <Switch
                size="md"
                label="Vis lengdeobjekter"
                checked={showLengthItemsInSelectedRoom}
                onChange={(event) => onShowLengthItemsInSelectedRoomChange(event.currentTarget.checked)}
              />
            </div>
          )}

          {showDeveloperControls && (
            <div
              style={{
                background: "white",
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "6px",
                padding: "0.25rem",
              }}
            >
              <SegmentedControl
                size="sm"
                value={useIfcCoveringCeilingGrids ? "ifc-covering" : "ifc-space"}
                data={[
                  { label: "IfcSpace grids", value: "ifc-space" },
                  { label: "IfcCovering grids", value: "ifc-covering" },
                ]}
                onChange={(value) => onUseIfcCoveringCeilingGridsChange(value === "ifc-covering")}
              />
            </div>
          )}

          {hasSelectedRooms && (
            <Group
              gap="xs"
              wrap="nowrap"
              style={{
                background: "white",
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "6px",
                padding: "0.25rem 0.375rem 0.25rem 0.5rem",
              }}
            >
              <Badge variant="light" color="gray">
                {selectedRoomCount} valgte rom
              </Badge>

              <Menu shadow="md" width={190}>
                <Menu.Target>
                  <Button
                    size="xs"
                    variant="light"
                    color="mc-primary"
                    leftSection={<IconPlus size={14} />}
                    rightSection={<IconChevronDown size={12} />}
                  >
                    Koble sak
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={onCreateIssueForSelectedRooms}>Ny sak</Menu.Item>
                  <Menu.Item onClick={onConnectExistingIssueForSelectedRooms}>Eksisterende sak</Menu.Item>
                </Menu.Dropdown>
              </Menu>

              <Tooltip label="Fjern valgte rom" withArrow>
                <ActionIcon variant="subtle" color="gray" size="sm" onClick={onClearSelectedRooms}>
                  <IconX size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}

          <TextInput
            size="xs"
            placeholder="Søk rom"
            value={roomSearchText}
            leftSection={<IconSearch size={14} />}
            style={{ width: 180 }}
            onChange={(event) => onRoomSearchTextChange(event.currentTarget.value)}
          />

          <Tooltip label="Juster vinkel" withArrow>
            <NumberInput
              value={floorplanRotation}
              step={90}
              allowDecimal={false}
              hideControls={false}
              leftSection={<IconRotateClockwise2 size={16} />}
              styles={{ input: { width: 120 } }}
              onChange={(value) => {
                if (typeof value === "number" && Number.isFinite(value)) {
                  onFloorplanRotationChange(value);
                }
              }}
            />
          </Tooltip>

          {manageIfcFilesUrl != null && (
            <Tooltip label="Håndter IFC-filer" withArrow>
              <ActionIcon
                component={Link}
                to={manageIfcFilesUrl}
                size="lg"
                variant="subtle"
                color="mc-primary"
                aria-label="Håndter IFC-filer"
              >
                <IconSettings size={18} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>
    </div>
  );
};

export default FloorplanActionBar;
